import { useMemo } from 'react';

import { Flex, Text } from '@raystack/apsara';
import clsx from 'clsx';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import styles from '@src/components/Custom/ModelCommonComponents/styles.module.css';

import { CategoricalTableProps } from './CategoricalTable';
import { childClass } from './CategoricalView';

export const CategoricalGraph = ({
  data,
  parentClass
}: CategoricalTableProps) => {
  const getPercentageArea = (area: string, classes: childClass[]) => {
    const totalArea = classes.reduce(
      (sum, item) => sum + Number(item['Area in km2']),
      0
    );

    return (Number(area) / totalArea) * 100;
  };

  const graphData = useMemo(() => {
    if (Object.keys(data).length > 0) {
      const currentChildClasses = data[parentClass] ?? [];

      const reducedChildClasses = currentChildClasses.reduce(
        (
          acc: {
            [key: string]: string | number;
          },
          value
        ) => {
          acc[value['Comparison class name (After)']] = getPercentageArea(
            value['Area in km2'],
            currentChildClasses
          );
          return acc;
        },
        {}
      );

      return [reducedChildClasses];
    }

    return [];
  }, [data, parentClass]);

  const barData = useMemo(() => {
    if (Object.keys(data).length > 0) {
      const currentChildClasses = data[parentClass] ?? [];

      const reducedArray = currentChildClasses.map(item => {
        return {
          key: item['Comparison class name (After)'],
          color: item['colors']
        };
      });

      return reducedArray;
    }
    return [];
  }, [data, parentClass]);

  //   This needs an common component
  // @ts-expect-error could not find interface for this
  const CustomToolTip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const content = payload[0] || {};
      const { dataKey = '', fill = '', value = 0 } = content;

      return (
        <Flex
          className={styles.graphTooltipWrapper}
          direction='column'
          gap='medium'
          align='start'
        >
          <Flex gap='small' align='center'>
            <div
              style={{
                width: 7,
                height: 7,
                background: fill,
                borderRadius: 99999
              }}
            />
            <Text weight={500} size={2}>
              {dataKey}
            </Text>
          </Flex>
          <Flex direction='column' align='start' gap='small'>
            <Flex align='center' justify='between'>
              <Text
                className={clsx(styles.toolTipKey, styles.toolTipSecondaryText)}
                weight={400}
                size={2}
              >
                Area conversion
              </Text>
              <Text
                weight={400}
                size={2}
                className={styles.toolTipSecondaryText}
              >
                : {isNaN(value) ? 'NA' : value.toFixed(3) + '%'}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      );
    }
  };

  return (
    <ResponsiveContainer height={68} width={'100%'}>
      <BarChart layout='vertical' data={graphData} stackOffset='expand'>
        <XAxis hide type='number' />
        <YAxis type='category' hide />
        <Tooltip
          shared={false}
          content={({ active, payload }) => (
            <CustomToolTip active={active} payload={payload} />
          )}
        />

        {barData.length > 0 &&
          barData.map(item => (
            <Bar
              key={item.key}
              dataKey={item.key}
              fill={item.color}
              stackId='a'
            />
          ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
